import { ApolloClient, gql, InMemoryCache } from '@apollo/client';
import { Amplify } from 'aws-amplify';
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  signIn,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage, Hub } from 'aws-amplify/utils';
import { PropsWithChildren, createContext, useEffect } from 'react';

import { AuthProviderName } from 'features/auth/types';
import { LOCAL_STORAGE_SIGNIN_ATTEMPTS, ROUTES } from 'shared/constants';
import {
  UserByEmailQuery,
  UserCompleteRegistrationInput,
  useUserCompleteRegistrationMutation,
  useUserSocialCompleteRegistrationMutation,
} from 'shared/graphql';
import { useHasSession, useLocalStorageState, usePushNotifications, useToast } from 'shared/hooks';
import {
  recordDebug,
  recordError,
  reportException,
  recordUser,
  stopRecordUser,
} from 'shared/utils/record';

import { useApolloContext } from './ApolloProvider';
import { useCommunityContext } from './CommunityProvider';

const AUTH_POOL_ID = process.env.REACT_APP_AUTH_POOL_ID;
const AUTH_CLIENT_ID = process.env.REACT_APP_AUTH_CLIENT_ID;
const AUTH_DOMAIN = process.env.REACT_APP_AUTH_DOMAIN;
const AUTH_STORAGE_DOMAIN = process.env.REACT_APP_AUTH_STORAGE_DOMAIN;

type UserChangePasswordInput = {
  resetCode: string;
  proposePassword: string;
  confirmProposePassword?: string;
  email: string;
};

const USER_BY_EMAIL_QUERY = gql`
  query UserByEmail($email: String!) {
    user(email: $email) {
      id
      email
    }
  }
`;

const USER_SEND_EMAIL_MUTATION = gql`
  mutation UserSendEmail($data: UserForgotPasswordEmailSendInput!) {
    userForgotPasswordEmailSend(data: $data) {
      success
    }
  }
`;

const AUTH_SIGNIN_REDIRECT_URL = `${window.location.origin}${ROUTES.public.login}`;
const AUTH_SIGNOUT_REDIRECT_URL = `${window.location.origin}`;

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: AUTH_POOL_ID,
      userPoolClientId: AUTH_CLIENT_ID,
      signUpVerificationMethod: 'code',
      loginWith: {
        oauth: {
          domain: AUTH_DOMAIN,
          scopes: ['email', 'profile', 'openid'],
          redirectSignIn: [AUTH_SIGNIN_REDIRECT_URL],
          redirectSignOut: [AUTH_SIGNOUT_REDIRECT_URL],
          // Social sign-in is not supported without 'code'.
          // https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-client-apps.html
          responseType: 'code',
        },
      },
    },
  },
});

// Setup the token provider to use cookies.
// https://docs.amplify.aws/react/build-a-backend/auth/concepts/tokens-and-credentials
cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    domain: AUTH_STORAGE_DOMAIN,
    secure: true,
  }),
);

const THRESHOLD_SIGNIN_ATTEMPTS = 3;

const MESSAGE_ALREADY_SESSION =
  'There is a session currently active, you cannot start a new one without sign-out';

// Use a separate instance of ApolloClient for authentication-related queries to avoid the links.
const apollo = new ApolloClient({
  uri: process.env.REACT_APP_ENDPOINT,
  cache: new InMemoryCache(),
});

function useAuthProvider() {
  const { selectedCommunity: school } = useCommunityContext();
  const { showMessage, showError, showSuccess, showWarning } = useToast();
  const { token, setToken } = useApolloContext();

  const [attempts, setAttempts] = useLocalStorageState(LOCAL_STORAGE_SIGNIN_ATTEMPTS, 0);
  const [registration] = useUserCompleteRegistrationMutation();
  const [socialRegistration] = useUserSocialCompleteRegistrationMutation();

  const pushNotifications = usePushNotifications();
  const hasSession = useHasSession();

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', message => {
      const event = message.payload.event;

      if (event === 'signedIn') {
        showSuccess('Successfully signed-in');
        checkSession();
      }

      if (event === 'tokenRefresh') {
        recordDebug('Token was refreshed');
        checkSession();
      }

      if (event === 'signedOut') {
        stopRecordUser();
        setToken(undefined);
      }
    });

    // Check the session for the first time
    // Then, depends on subscriptions to the auth-flow
    checkSession();

    return () => {
      unsubscribe();
    };
  }, []);

  /** Increase by one the current attempts of sign-in. */
  const increaseSignInAttempts = () => {
    setAttempts(prev => prev + 1);

    if (attempts < THRESHOLD_SIGNIN_ATTEMPTS) {
      window.location.reload();
    }
  };

  /** Checks the current session, refresh the current token and verifies the user. */
  const checkSession = async ({ forceRefresh = false } = {}): Promise<void> => {
    recordDebug(`Current sign-in attempts: ${attempts}`);

    if (attempts >= THRESHOLD_SIGNIN_ATTEMPTS) {
      showError(`Your session expired, please sign in again`, { reportable: false });
      return await logout();
    }

    try {
      const session = await fetchAuthSession({ forceRefresh });

      if (forceRefresh && !session?.tokens) {
        recordDebug('Session was refreshed but no tokens were found, user has no session');

        if (hasSession) {
          // Ensures we clean-up the session completely
          logout();
        }

        return;
      }

      if (!session.tokens) {
        recordDebug('No tokens were found on current session. Trying to refresh the session');
        return checkSession({ forceRefresh: true });
      }

      const newToken = session.tokens.idToken?.toString();
      const payload = session.tokens.idToken?.payload;

      if (!newToken) {
        recordDebug(`No ID token was found on current session`);
        return increaseSignInAttempts();
      }

      if (!payload) {
        recordDebug(`No payload was found on current session`);
        return increaseSignInAttempts();
      }

      const email = String(payload?.email);

      setToken(newToken);
      recordDebug(`Session found: ${email}`);
      recordDebug(`Setting up token on GraphQL provider`);

      const response = await apollo.query<UserByEmailQuery>({
        query: USER_BY_EMAIL_QUERY,
        errorPolicy: 'ignore',
        context: { headers: { authorization: `Bearer ${newToken}` } },
        variables: { email },
      });

      if (!response.data?.user?.id) {
        recordDebug(`No user was found with email: ${email}`);

        // Must be registered first before sign-in
        // Otherwise, the account does not match with any 8base user
        await logout();
        return;
      }

      await pushNotifications.setCurrentUser(response.data.user.id);

      recordDebug(`User found: ${response.data.user.id}`);
      recordUser(email, response.data.user.id);

      if (email) {
        // Checks whether the `email` is fully registered. Otherwise, will setup roles and permissions
        await socialRegistration({
          variables: { data: { email }, community: { id: school?.id } },
          // Use the same token as the one used to verify the user
          // Otherwise, the mutation will fail because the user is not registered
          context: { headers: { authorization: `Bearer ${newToken}` } },
          errorPolicy: 'ignore',
          client: apollo,
        });
      }
    } catch (err) {
      recordError(err);
      increaseSignInAttempts();

      if (err instanceof Error) {
        showError(err.message);
      }
    }
  };

  /**
   * Sign-up a new user using `email` and `password`.
   * Creates an account on AWS Cognito and sign-in with the given token.
   */
  const register = async (data: UserCompleteRegistrationInput) => {
    if (hasSession) {
      showWarning(MESSAGE_ALREADY_SESSION);
      return;
    }

    if (!school) {
      return;
    }

    try {
      const response = await registration({
        variables: { data, community: { id: school?.id } },
      });

      const token = response?.data?.commit?.accessToken;

      if (!token) {
        showError(`No token found`, { reportable: false });
        return;
      }

      setToken(token);
    } catch (err) {
      recordError(err);

      if (err instanceof Error) {
        showError(err.message);
      }
    }
  };

  /**
   * Sign-in using `email` and `password`.
   * Checks the credentials on AWS Cognito and start a new session with the given token.
   */
  const login = async (data: { email: string; password: string }) => {
    if (hasSession) {
      // Do not reload, will create a loop.
      showWarning(MESSAGE_ALREADY_SESSION);
      return;
    }

    try {
      await signIn({ username: data.email, password: data.password });
    } catch (err) {
      recordError(err);

      if (err instanceof Error) {
        showError(err.message, { reportable: false });
      }
    }
  };

  /**
   * Sign-out and purge the credential-related data.
   */
  const logout = async (): Promise<void> => {
    recordDebug(`Closing session`);

    try {
      const [sessionClosed, apolloReset, notificationsClosed] = await Promise.allSettled([
        signOut(),
        apollo.resetStore(),
        pushNotifications.resetCurrentUser(),
      ]);

      recordDebug(`Session closed: ${sessionClosed.status}`);
      recordDebug(`Apollo reset: ${apolloReset.status}`);
      recordDebug(`Notifications reset: ${notificationsClosed.status}`);

      stopRecordUser();
      setToken(undefined);
      localStorage.clear();
    } catch (err) {
      reportException(err);
    }

    // Refresh the page to ensure the session is closed.
    location.reload();
  };

  /**
   * Sign-in using a redirect to the given `provider`.
   * To follow the authentication flow with a provider, when AWS Cognito return to the application use the {@link confirmSocialSignIn} function.
   */
  const socialSignIn = async (provider: AuthProviderName) => {
    if (token) {
      // Do not reload, will create a loop.
      showWarning(MESSAGE_ALREADY_SESSION);
      return;
    }

    try {
      if (provider === AuthProviderName.Google) {
        await signInWithRedirect({ provider: 'Google' });
      }

      if (provider === AuthProviderName.Facebook) {
        await signInWithRedirect({ provider: 'Facebook' });
      }

      if (provider === AuthProviderName.LinkedIn) {
        await signInWithRedirect({ provider: { custom: 'LinkedIn' } });
      }
    } catch (err) {
      recordError(err);
      showError(`Something went wrong sign-in with "${provider}"`);
    }
  };

  /**
   * Sign-in using a provider but using {@link socialSignIn} first.
   * Use when AWS Cognito redirects to the application with the `code`.
   */
  const confirmSocialSignIn = async (code: string) => {
    if (token) {
      return;
    }

    showMessage(`Checking sign-in confirmation code`);

    try {
      await confirmSignIn({ challengeResponse: code });
    } catch (err) {
      recordError(err);
    }
  };

  /**
   * Send an email to restore the password.
   */
  const forgotPassword = async (email: string): Promise<string | undefined> => {
    if (token) {
      return;
    }

    try {
      const userQueryResponse = await apollo.query({
        query: USER_BY_EMAIL_QUERY,
        fetchPolicy: 'network-only',
        variables: {
          email,
        },
      });

      if (!userQueryResponse.data.user) {
        showError('Submitted email was not found. Please try again.', { reportable: false });
        return;
      }

      await apollo.mutate({
        mutation: USER_SEND_EMAIL_MUTATION,
        fetchPolicy: 'no-cache',
        variables: {
          data: {
            email,
          },
        },
      });

      const blurredEmail = `${email.slice(0, 2)}***@***${email.slice(email.lastIndexOf('.'))}`;

      return blurredEmail;
    } catch (err) {
      recordError(err);

      if (err instanceof Error) {
        showError(err.message);
      }
    }
  };

  /**
   * Verify the `resetCode` and set the new `proposePassword` to the given `email`.
   */
  const changePassword = async ({ proposePassword, resetCode, email }: UserChangePasswordInput) => {
    if (token) {
      return;
    }

    try {
      await confirmResetPassword({
        username: email,
        newPassword: proposePassword,
        confirmationCode: resetCode,
      });
    } catch (err) {
      recordError(err);

      if (err instanceof Error) {
        showError(err.message);
      }
    }
  };

  return {
    /** @deprecated Use `hasSession` instead. */
    isAuthenticated: hasSession,
    hasSession,
    login,
    logout,
    register,
    socialSignIn,
    confirmSocialSignIn,
    forgotPassword,
    changePassword,
    checkSession,
  };
}

export type AuthContextType = ReturnType<typeof useAuthProvider>;

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function AuthProvider(props: PropsWithChildren<unknown>) {
  const context = useAuthProvider();

  return <AuthContext.Provider value={context}>{props.children}</AuthContext.Provider>;
}
